import * as THREE from 'three';

/**
 * Ghost racing. A run is recorded as a sparse sample track (time, position,
 * yaw) and the best one per level is replayed as a hologram you race against.
 * The track format is versioned and dumb on purpose: it is destined to be
 * shared (URLs, leaderboards) and replayed by things that are not this build.
 */

const RATE = 0.1;          // seconds between samples
// Ten minutes plus slack: the board's supported run length (validator
// MAX_TIME, 600s) at 10Hz. The recorder used to stop at five minutes
// while the board accepted ten, so a slow Chapter 2 clear recorded a
// track that ended early, the validator refused it, and the client
// swallowed the refusal: the newest sectors could not take records.
const MAX_SAMPLES = 6100;

export class GhostRecorder {
  constructor() { this.reset(); }

  reset() {
    this.t = 0;
    this._next = 0;
    this.samples = [];
  }

  /** Call once per frame while the run is live. */
  update(dt, pos, yaw) {
    this.t += dt;
    if (this.t < this._next || this.samples.length >= MAX_SAMPLES) return;
    this._next += RATE;
    this.samples.push([
      +this.t.toFixed(2),
      +pos.x.toFixed(2), +pos.y.toFixed(2), +pos.z.toFixed(2),
      +yaw.toFixed(3),
    ]);
  }

  /** Freeze the recording into a shareable track. */
  track(finishTime) {
    return { v: 1, time: +finishTime.toFixed(2), samples: this.samples.slice() };
  }
}

/** Replays a track through a hologram body. No collider: a ghost is a memory. */
export class GhostRider {
  constructor(scene) {
    this.track = null;
    this.t = 0;
    this._i = 0;

    this.group = new THREE.Group();
    this.group.visible = false;
    scene.add(this.group);

    const body = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.42, 0.96, 6, 16),
      new THREE.MeshBasicMaterial({
        color: 0x2ff3ff,
        transparent: true,
        opacity: 0.22,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    body.position.y = 0.9;
    this.group.add(body);

    const visor = new THREE.Mesh(
      new THREE.BoxGeometry(0.34, 0.09, 0.06),
      new THREE.MeshBasicMaterial({
        color: 0xd8fbff,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    visor.position.set(0, 1.52, -0.42);
    this.group.add(visor);
  }

  get active() { return this.track !== null; }

  start(track) {
    if (!track || track.v !== 1 || !Array.isArray(track.samples) || track.samples.length < 2) {
      this.stop();
      return false;
    }
    this.track = track;
    this.t = 0;
    this._i = 0;
    this.group.visible = true;
    const s = track.samples[0];
    this.group.position.set(s[1], s[2], s[3]);
    this.group.rotation.y = s[4];
    return true;
  }

  stop() {
    this.track = null;
    this.group.visible = false;
  }

  update(dt) {
    if (!this.track) return;
    this.t += dt;
    const ss = this.track.samples;

    // Past the end: the ghost finished. Linger a moment, then dissolve.
    if (this.t >= ss[ss.length - 1][0]) {
      if (this.t > ss[ss.length - 1][0] + 2) this.group.visible = false;
      return;
    }

    while (this._i < ss.length - 2 && ss[this._i + 1][0] <= this.t) this._i++;
    const a = ss[this._i];
    const b = ss[this._i + 1];
    const span = b[0] - a[0];
    let f = span > 1e-6 ? THREE.MathUtils.clamp((this.t - a[0]) / span, 0, 1) : 1;

    // A portal traversal is a discontinuity: lerping it would streak the
    // hologram across the level. Snap instead.
    const jump = Math.hypot(b[1] - a[1], b[2] - a[2], b[3] - a[3]);
    if (span > 1e-6 && jump / span > 160) f = f < 0.5 ? 0 : 1;

    this.group.position.set(
      a[1] + (b[1] - a[1]) * f,
      a[2] + (b[2] - a[2]) * f,
      a[3] + (b[3] - a[3]) * f,
    );
    // Yaw lerps the short way round, or a ghost turning 350 degrees spins.
    let dy = b[4] - a[4];
    if (dy > Math.PI) dy -= Math.PI * 2;
    if (dy < -Math.PI) dy += Math.PI * 2;
    this.group.rotation.y = a[4] + dy * f;
  }

  dispose() {
    this.group.removeFromParent();
    this.group.traverse((o) => { if (o.isMesh) { o.geometry.dispose(); o.material.dispose(); } });
  }
}
